import { Box, Button, Checkbox, Flex, Heading, ListItem, Text, UnorderedList } from '@chakra-ui/react';
import Link from 'next/link';
import { ChangeEvent, FC, ReactNode, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorMessage } from '@/components/core/inputs/ErrorMessage';
import { Field } from '@/components/core/inputs/Field';
import { ISignupStepsProps, SignUpContext } from '@/containers/SignUp/components/SignUpContext';
import { PASSWORD_REGEX } from '@/utils/constants';

interface IPasswordValidation {
  containsUppercase: boolean;
  containsLowercase: boolean;
  isValidLength: boolean;
  containsDigit: boolean;
}

interface ICreatePasswordStep {
  onSubmit: (passwordData: Partial<ISignupStepsProps>) => void;
}

export const CreatePasswordStep: FC<ICreatePasswordStep> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm();
  const { setCurrentStep } = useContext(SignUpContext);
  const [passwordValidation, setPasswordValidation] = useState<IPasswordValidation>({
    containsLowercase: false,
    containsDigit: false,
    containsUppercase: false,
    isValidLength: false,
  });

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>) {
    const { value: inputValue } = event.target;

    const onChangePasswordValidation: IPasswordValidation = {
      containsLowercase: /.*[a-z].*/.test(inputValue),
      containsUppercase: /.*[A-Z].*/.test(inputValue),
      containsDigit: /.*\d.*/.test(inputValue),
      isValidLength: inputValue.length >= 8,
    };

    if (JSON.stringify(onChangePasswordValidation) !== JSON.stringify(passwordValidation)) {
      setPasswordValidation(onChangePasswordValidation);
    }
  }

  function handlePasswordSubmit(passwordData: Partial<ISignupStepsProps>) {
    setCurrentStep((cs) => cs + 1);
    onSubmit(passwordData);
  }

  const LIST_IMAGE_URL = "url('icons/ic-check.svg')";

  return (
    <Flex w="full" justifyContent="space-around" flexDirection={{ base: 'column', lg: 'row' }}>
      <Flex mb={14} maxW="390px" gap={8} flexDirection="column" flex={1}>
        <Text color="gray.600">{`3 / 3`}</Text>
        <Heading fontSize="5xl">{t('createAccount.password.title')}</Heading>
        <Text mt={13}>{t('createAccount.password.description')}</Text>
        <UnorderedList>
          <ListItem color="gray.500" mb="1" listStyleImg={passwordValidation.isValidLength && LIST_IMAGE_URL}>
            8 characters
          </ListItem>
          <ListItem color="gray.500" mb="1" listStyleImg={passwordValidation.containsUppercase && LIST_IMAGE_URL}>
            one uppercase letter
          </ListItem>
          <ListItem color="gray.500" mb="1" listStyleImg={passwordValidation.containsLowercase && LIST_IMAGE_URL}>
            one lowercase letter
          </ListItem>
          <ListItem color="gray.500" listStyleImg={passwordValidation.containsDigit && LIST_IMAGE_URL}>
            one number
          </ListItem>
        </UnorderedList>
      </Flex>
      <Flex flexDirection="column" maxW="390px" flex={1}>
        <Box as="form" onSubmit={handleSubmit(handlePasswordSubmit)}>
          <Box>
            <Field
              {...register('password', {
                required: 'Field is required',
                pattern: {
                  value: PASSWORD_REGEX,
                  message: 'The password you entered does not meet the requirements.',
                },
                onChange: (e) => handlePasswordChange(e),
              })}
              label="Your password"
              type="password"
              hasValue={Boolean(watch('password'))}
              isError={Boolean(errors['password'])}
            />
            <ErrorMessage errors={errors} name="password" />
          </Box>
          <Box mt={6}>
            <Field
              {...register('passwordConfirmation', {
                required: 'Field is required',
                validate: (value) => value === watch('password') || "Passwords don't match",
              })}
              label="Repeat password"
              type="password"
              hasValue={Boolean(watch('passwordConfirmation'))}
              isError={Boolean(errors['passwordConfirmation'])}
            />
            <ErrorMessage errors={errors} name="passwordConfirmation" />
          </Box>

          <Box my={10}>
            <Checkbox
              lineHeight={1}
              spacing={4}
              size="lg"
              color="black"
              {...register('termsOfUse', { required: 'Please confirm terms of use' })}
            >
              <Text>
                I agree with the{' '}
                <Link target="_blank" href={`${process.env.NEXT_PUBLIC_BASE_URL}terms-of-use`}>
                  <Text as="span" textDecoration="underline">
                    Terms of Use
                  </Text>
                </Link>
                .
              </Text>
            </Checkbox>
            <ErrorMessage errors={errors} name="termsOfUse" />
          </Box>
          <Button variant="outline" w="130px" type="submit">
            {t('action.next')}
          </Button>
        </Box>
      </Flex>
    </Flex>
  );
};
